/**
 * The live proposal: one redline card's preview of what Apply would write,
 * kept current while the user edits the document.
 *
 * A `LiveProposal` follows the page store (`DocumentPageStore`), the one owner
 * of what Word holds for every bookmark the add-in shows: when the store reads
 * the page after a document change, the card rebuilds its preview from that
 * read when its paragraphs or tables read differently, with no Word read of its
 * own. Word is read by the card only through the anchor tracker, when one of
 * its bookmarks is gone from the page and recovery has to look for it. React
 * subscribes to the state through `subscribe`; the class itself knows nothing
 * of React, so it can be driven from a test.
 */
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { ON_PARAGRAPH, type RedlinePlacement } from "@common/utils/redline/wire/redline-placement";
import type { RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";

import { AnchorLog } from "../anchor-log";
import type { AnchoredRead, AnchorTracker, AttachedRedline } from "../anchor-tracker";
import { AnchoredProposal, type BuiltProposal, type LayeredEditPackage } from "./anchored-proposal";
import { RedlineRegionDisagreementReport } from "../../redline-region-disagreement-report";
import type { BodyReplacementPhase } from "../body-replacement";
import type { DocumentPage, DocumentPageStore } from "../document-page-store";
import type { RecoveryAttempt, RecoveryTrigger } from "../recovery/recovery-chain";
import type { PageParagraphRead, TableRead, WordDocument } from "../word-document";

export type LiveProposalSource = "seed" | "event" | "selection" | "refind";

export interface LiveProposalState {
    /**
     * CASES
     *   - "off"      -> nothing to follow
     *   - "seeding"  -> Word is being read for the first time, or again after a body replacement
     *   - "proposed" -> the redline over the text it was proposed against, published before Word
     *                   has been read; the preview to show, never the package to write
     *   - "live"     -> the redline over the page as it stands; the package Apply All writes
     *   - "error"    -> the paragraph could not be found or the engine failed
     */
    status: "off" | "seeding" | "proposed" | "live" | "error";
    previewPackageOoxml: string | null;
    /**
     * The proposal's own package, rows and tables composed, before the user's edit
     * is laid over it: the structure the card's editor lays its fields over. Null
     * until a package was built; the same string as `previewPackageOoxml` for a
     * card nobody edited.
     */
    proposalPackageOoxml: string | null;
    changeCount: number;
    error: string | null;
    eventsSupported: boolean;
    lastSource: LiveProposalSource | null;
    lastUpdatedAtMs: number | null;
    lastReadMs: number | null;
    lastEngineMs: number | null;
    refreshCount: number;
    bookmarkRepairCount: number;
    recovery: RecoveryAttempt | null;
    placement: RedlinePlacement;
    stampedAt: string | null;
    refinding: boolean;
    /** True when recovery stopped before the server step, which only Apply or Refind may run. */
    serverAwaitsUserAction: boolean;
    /**
     * True when the error is that the card's bookmark is gone from the page and no
     * recovery step found its paragraph: the one error Refind can do anything about.
     * False for every other error, an engine refusal among them.
     */
    bookmarkLost: boolean;
    /**
     * True when the error is the engine's: building the card over the page its paragraph
     * was found on threw (a revert whose change is no longer there as it was read, an
     * edit the engine hands back). Apply builds the same card over the same page and meets
     * the same error, so the card holds Apply back with it (`RedlineSection`).
     *
     * CASES
     *   - the engine threw building the card, or the
     *     card's redline did not parse                  -> true
     *   - Word failed a read, the bookmark is lost, the
     *     server could not place it, or nothing failed  -> false: a later read or Refind may
     *                                                      clear it, and Apply reads again
     */
    cardBuildFailed: boolean;
    /** One line per row or table change the card asks for (`TableChangePreview`), shown under the preview; empty without table changes. */
    tableChangeLines: string[];
}

export const LIVE_PROPOSAL_OFF: LiveProposalState = {
    status: "off",
    previewPackageOoxml: null,
    proposalPackageOoxml: null,
    changeCount: 0,
    error: null,
    eventsSupported: false,
    lastSource: null,
    lastUpdatedAtMs: null,
    lastReadMs: null,
    lastEngineMs: null,
    refreshCount: 0,
    bookmarkRepairCount: 0,
    recovery: null,
    placement: ON_PARAGRAPH,
    stampedAt: null,
    refinding: false,
    serverAwaitsUserAction: false,
    bookmarkLost: false,
    cardBuildFailed: false,
    tableChangeLines: [],
};

export interface LiveProposalInput {
    bookmarkNames: string[];
    redline: AttachedRedline;
    /** The user's edit of the card, layered over the proposal in the preview under the user's name; null for a card nobody edited. */
    editLayer: ProposalEditLayerInput | null;
    tracker: AnchorTracker;
    word: WordDocument;
    /** The page store every card and the drawer read the document through; the card follows its bookmarks there. */
    pages: DocumentPageStore;
    /**
     * Whether the card looks for its paragraph through the anchor tracker, a Word
     * read of its own, when a bookmark of it is gone from the page. Starts as given
     * and follows `setRecoversLostParagraph`.
     *
     * FALLBACK
     *   Left out, the card recovers, as a chat card always has.
     */
    recoversLostParagraph?: boolean;
}

export interface ProposalEditLayerInput {
    textRedline: RedlineTextProposal;
    author: string;
    /**
     * The card as the edit box opened on it (`ProposalEditLayer.textRedlineWhenOpened`).
     *
     * NULL
     *   An edit saved before the box recorded it: the layer is merged from the proposal's own texts.
     */
    textRedlineWhenOpened: RedlineTextProposal | null;
}

/** The proposal's composed package as last published, the change count shown for it, and the stamp its revisions carry. */
interface ComposedProposalPackage {
    packageOoxml: string;
    baseChangeCount: number;
    stampedAt: string;
}

/** One second after the proposal's stamp: a distinct `w:date` for the user's layer, so relabelling the proposal's stamp leaves it alone. */
const EDIT_LAYER_STAMP_OFFSET_MS = 1_000;

export function editLayerStampAfter(stampedAt: string): string {
    return new Date(Date.parse(stampedAt) + EDIT_LAYER_STAMP_OFFSET_MS).toISOString();
}

/** What a card's preview before Word is read is built from: its bookmarks, which name it in the log, its redline, and the user's edit of it. */
export interface PreviewBeforeReadInput {
    bookmarkNames: string[];
    redline: AttachedRedline;
    editLayer: ProposalEditLayerInput | null;
}

/**
 * The package a card shows before Word is read, the stamp the proposal's own
 * revisions carry in it, and its table change lines: none, since those name
 * rows of a table only a read of Word holds.
 */
export interface PreviewBeforeRead {
    previewPackageOoxml: string;
    stampedAt: string;
    tableChangeLines: string[];
}

export class LiveProposal {
    /** Each card names its own interest in the page store, so two cards on one bookmark stay independent. */
    private static nextOwnerSerial = 0;
    /**
     * WHY a card that lost its paragraph waits longer between event-driven refreshes
     *   It has no paragraph ids to filter events by, so every change anywhere would
     *   send it back through reconcile and recovery, a write-locked read each time.
     *   Once a quarter minute is enough for a paragraph that may come back; Refind
     *   with AI is the user's fast path.
     */
    private static readonly LOST_CARD_EVENT_COOL_DOWN_MS = 15_000;
    /**
     * WHY refreshes pass through a gate
     *   Every refresh is a Word read of several round trips. Twenty cards refreshing
     *   at once held Word's lock for seconds, and a Go to click, a write, waited
     *   behind them all. Two at a time keeps a click's wait to a read or two, and a
     *   pending write stops new refreshes from starting until it has run.
     */
    private static readonly MAX_CONCURRENT_REFRESHES = 2;
    private static readonly REFRESH_GATE_POLL_MS = 100;
    private static activeRefreshCount = 0;

    private static async acquireRefreshSlot(word: WordDocument): Promise<void> {
        while (LiveProposal.activeRefreshCount >= LiveProposal.MAX_CONCURRENT_REFRESHES || word.pendingForegroundWork() > 0) {
            await new Promise<void>((resolve) => window.setTimeout(resolve, LiveProposal.REFRESH_GATE_POLL_MS));
        }
        LiveProposal.activeRefreshCount += 1;
    }

    private static releaseRefreshSlot(): void {
        LiveProposal.activeRefreshCount -= 1;
    }

    /**
     * The preview a card shows before Word is read, built from the card alone: its
     * redline over the text it was proposed against, at the placement every card
     * starts on, with the user's edit laid over it as `publishBeforeRead` lays it.
     *
     * WHY
     *   An applied card keeps the look it had when Apply was clicked. When the
     *   preview it showed was not kept (it was applied while off screen, or the pane
     *   was reloaded since), this is the preview the card shows before its page
     *   read, and the one look that needs neither Word nor anything stored.
     *
     * NULL
     *   The redline does not parse, or the engine refuses it; the failure is logged.
     */
    static previewBeforeRead({ bookmarkNames, redline, editLayer }: PreviewBeforeReadInput): PreviewBeforeRead | null {
        try {
            const proposal = new AnchoredProposal(redline, "asTrackedChange");
            const built = proposal.buildBeforeRead({ placement: ON_PARAGRAPH, stampedAt: new Date().toISOString() });
            const { stampedAt } = built.outcome;
            /**
             * CASES
             *   - nobody edited the card -> the built package as it is
             *   - the user edited it     -> the edit laid over it under the user's name, stamped after the proposal
             */
            const previewPackageOoxml = editLayer === null
                ? built.outcome.packageOoxml
                : proposal.layerEditOverPackage(built.outcome.packageOoxml, stampedAt, { ...editLayer, stampedAt: editLayerStampAfter(stampedAt) }).packageOoxml;
            return { previewPackageOoxml, stampedAt, tableChangeLines: [] };
        } catch (error) {
            AnchorLog.record({ kind: "error", bookmarkNames, step: "proposal before read", message: AnchorLog.messageOf(error) });
            return null;
        }
    }

    private state: LiveProposalState = LIVE_PROPOSAL_OFF;
    private readonly listeners = new Set<(state: LiveProposalState) => void>();
    private readonly proposal: AnchoredProposal | null;
    private readonly parseError: string | null;
    private disposed = false;
    private inFlight: Promise<void> | null = null;
    private lastRefreshedAtMs = 0;
    private pendingSource: LiveProposalSource | null = null;
    /**
     * The paragraphs and tables of the card as the page last built from read them,
     * joined into one key; null before the first build and after a body swap.
     *
     * WHY
     *   The store publishes the page after any change in the document. The card
     *   rebuilds only when what it reads from the page differs, so typing elsewhere
     *   costs it a string comparison and no engine run.
     */
    private lastBuiltInputsKey: string | null = null;
    private readonly ownerKey = `card-${LiveProposal.nextOwnerSerial++}`;
    private replacingBody = false;
    private subscriptions: Array<() => void | Promise<void>> = [];
    /** The user's edit of the card as last given; starts as the input's and follows `setEditLayer`. */
    private editLayer: ProposalEditLayerInput | null;
    /** The edit layer as last given, serialised, so an equal edit given again is not laid over the package a second time. */
    private editLayerKey: string | null;
    /** Whether a lost bookmark is looked for through the anchor tracker; starts as the input's and follows `setRecoversLostParagraph`. */
    private recoversLostParagraph: boolean;
    /**
     * The proposal's last composed package, before any edit layer, with the count
     * the panel shows for it and the stamp it was built under, so a changed edit is
     * laid over it again without reading Word. Null until the first package.
     */
    private lastComposed: ComposedProposalPackage | null = null;

    constructor(private readonly input: LiveProposalInput) {
        this.editLayer = input.editLayer;
        this.editLayerKey = LiveProposal.editLayerKeyOf(input.editLayer);
        this.recoversLostParagraph = input.recoversLostParagraph ?? true;
        try {
            /** A preview draws a revert of the author's own change as a tracked change; Apply builds its own proposal and writes it as Word does. */
            this.proposal = new AnchoredProposal(input.redline, "asTrackedChange");
            this.parseError = null;
        } catch (error) {
            this.proposal = null;
            this.parseError = AnchorLog.messageOf(error);
        }
    }

    /**
     * CASES
     *   - the card has no edit  -> the composed package as it is, no changes of its own
     *   - the card has an edit  -> the edit layered over it under the user's name, stamped after the proposal
     */
    private withEditLayer(composedPackageOoxml: string, stampedAt: string): LayeredEditPackage {
        if (this.editLayer === null || this.proposal === null) return { packageOoxml: composedPackageOoxml, layerChangeCount: 0 };
        return this.proposal.layerEditOverPackage(composedPackageOoxml, stampedAt, { ...this.editLayer, stampedAt: editLayerStampAfter(stampedAt) });
    }

    /**
     * The user's edit changed while the card is up: the new edit is laid over the
     * package last composed and published in place, so the preview updates without a
     * read, a reconcile or a remount.
     *
     * WHEN no package has been composed yet
     *   CONSEQUENCE
     *     only the field changes; the first publish lays the edit over its package.
     */
    setEditLayer(editLayer: ProposalEditLayerInput | null): void {
        const editLayerKey = LiveProposal.editLayerKeyOf(editLayer);
        /**
         * WHEN the edit given is the one already laid
         *   CONSEQUENCE
         *     nothing changes: the chat card and the drawer both hand the card its edit,
         *     and the second hand-over must not lay it again.
         */
        if (editLayerKey === this.editLayerKey) return;
        this.editLayer = editLayer;
        this.editLayerKey = editLayerKey;
        const composed = this.lastComposed;
        if (composed === null) return;
        try {
            const layered = this.withEditLayer(composed.packageOoxml, composed.stampedAt);
            this.setState({ ...this.state, previewPackageOoxml: layered.packageOoxml, changeCount: composed.baseChangeCount + layered.layerChangeCount, lastUpdatedAtMs: Date.now() });
        } catch (error) {
            AnchorLog.record({ kind: "error", bookmarkNames: this.input.bookmarkNames, step: "edit layer", message: AnchorLog.messageOf(error) });
        }
    }

    /** The serialised edit layer, the one form two hand-overs of the same edit compare equal in; null for no edit. */
    private static editLayerKeyOf(editLayer: ProposalEditLayerInput | null): string | null {
        return editLayer === null ? null : JSON.stringify(editLayer);
    }

    /**
     * Turns the search for a lost paragraph on or off: on while a view that shows
     * the card's recovery (the chat card) holds it, off while only views that read
     * the page (the drawer, a collision hover) do.
     *
     * WHEN it is turned on while a bookmark of the card is gone from the page
     *   CONSEQUENCE
     *     the card looks for it at once, as it would have on the page that found it gone.
     */
    setRecoversLostParagraph(recoversLostParagraph: boolean): void {
        const turnedOn = recoversLostParagraph && !this.recoversLostParagraph;
        this.recoversLostParagraph = recoversLostParagraph;
        if (turnedOn && this.state.status !== "off") this.onPage(this.input.pages.page);
    }

    /** The state as last published: what a view that takes hold of the card later draws first. */
    get currentState(): LiveProposalState {
        return this.state;
    }

    /** Calls the listener with the current state at once and on every change; returns the unsubscribe. */
    subscribe(listener: (state: LiveProposalState) => void): () => void {
        this.listeners.add(listener);
        listener(this.state);
        return () => {
            this.listeners.delete(listener);
        };
    }

    /** Seeds the preview and starts following the document. */
    start(): void {
        /** The page store follows the document's event stream for every card; the card follows the store, and hears a body replacement on the stream directly. */
        const follow = (): void => {
            /**
             * A snapshot restore replaces the body: every id the card follows dies and
             * its bookmark comes back with the restored file. Events are held while the
             * write runs, then the card seeds itself again so the preview is rebuilt
             * against the restored paragraphs; a bookmark the restored file lacks goes
             * through recovery exactly as a lost one does.
             */
            const onBodyReplaced = (phase: BodyReplacementPhase): void => {
                if (this.disposed) return;
                AnchorLog.record({ kind: "body_replaced", bookmarkNames: this.input.bookmarkNames, phase });
                /**
                 * WHEN a whole-body replacement begins
                 *   CONSEQUENCE
                 *     - pause live following until reseeding
                 *     - clear tracked paragraph ids because the body swap invalidates them
                 */
                if (phase === "begin") {
                    this.replacingBody = true;
                    this.pendingSource = null;
                    this.setState({ ...this.state, status: "seeding", cardBuildFailed: false });
                    return;
                }
                this.replacingBody = false;
                /** The restored paragraphs read differently from anything built before: the next page rebuilds the card whatever it reads. */
                this.lastBuiltInputsKey = null;
            };
            this.subscriptions.push(
                this.input.word.events.subscribe((event) => {
                    if (event.kind === "body_replaced") onBodyReplaced(event.phase);
                }),
            );
            /**
             * The card follows its bookmarks in the page store, which reads Word once per
             * document change for every card and the drawer together; `onPage` decides
             * whether this card has to rebuild or recover.
             */
            this.subscriptions.push(this.input.pages.watch(this.ownerKey, this.input.bookmarkNames));
            this.subscriptions.push(this.input.pages.subscribe((page) => this.onPage(page)));
        };
        const proposalIsMissing = this.proposal === null;
        const proposalHasChanges = proposalIsMissing ? false : this.proposal.hasChangesToApply;
        const proposalCannotBeFollowed = proposalIsMissing || !proposalHasChanges;
        /**
         * WHEN the redline did not produce an anchored proposal or has nothing to write
         *   CONSEQUENCE
         *     stop before subscribing -> there is nothing live to follow
         */
        if (proposalCannotBeFollowed) {
            /**
             * CASES
             *   - parse failed              -> status "error" with the parse message, a failed build of
             *                                  the card: Apply parses the same redline and fails alike
             *   - no paragraph anchors only -> status "off"
             */
            this.setState({ ...LIVE_PROPOSAL_OFF, status: this.parseError ? "error" : "off", error: this.parseError, cardBuildFailed: this.parseError !== null });
            return;
        }
        /**
         * WHEN following the document throws (no Word to ask, a subscription refused)
         *   CONSEQUENCE
         *     this card alone shows the error; every other card, and the drawer, carry on.
         */
        try {
            const eventsSupported = this.input.word.events.wordChangesSupported();
            this.setState({ ...LIVE_PROPOSAL_OFF, status: "seeding", eventsSupported });
            this.publishBeforeRead();
            AnchorLog.record({ kind: "watch", bookmarkNames: this.input.bookmarkNames, eventsSupported });
            follow();
        } catch (error) {
            this.setState({ ...this.state, status: "error", error: AnchorLog.messageOf(error), cardBuildFailed: false });
        }
    }

    /**
     * WHEN the card has just arrived and Word has not been read yet
     *   CONSEQUENCE
     *     publish the redline over the text it was proposed against as status
     *     "proposed", so the card shows its redline at once; the seed read that
     *     follows replaces it with the redline over the page. A failure here costs
     *     only the early preview: the seed read still runs.
     */
    private publishBeforeRead(): void {
        if (!this.proposal) return;
        try {
            const stampedAt = new Date().toISOString();
            const built = this.proposal.buildBeforeRead({ placement: this.input.tracker.placement, stampedAt });
            /**
             * EXAMPLE
             *   a rows card changes no paragraph text -> paragraph changeCount 0 + 1 row = 1, so the
             *   card shows its row line at once instead of "already reads as this redline proposes"
             */
            const baseChangeCount = built.outcome.changeCount + this.proposal.tableChangeCount;
            /** Before Word is read there is no table to compose rows into: the built package is the composed one. */
            this.lastComposed = { packageOoxml: built.outcome.packageOoxml, baseChangeCount, stampedAt: built.outcome.stampedAt };
            const layered = this.withEditLayer(built.outcome.packageOoxml, built.outcome.stampedAt);
            this.setState({
                ...this.state,
                status: "proposed",
                previewPackageOoxml: layered.packageOoxml,
                proposalPackageOoxml: built.outcome.packageOoxml,
                changeCount: baseChangeCount + layered.layerChangeCount,
                stampedAt: built.outcome.stampedAt,
                lastSource: "seed",
                lastUpdatedAtMs: Date.now(),
                cardBuildFailed: false,
            });
        } catch (error) {
            AnchorLog.record({ kind: "error", bookmarkNames: this.input.bookmarkNames, step: "proposal before read", message: AnchorLog.messageOf(error) });
        }
    }

    stop(): void {
        this.disposed = true;
        for (const unsubscribe of this.subscriptions) void unsubscribe();
        this.subscriptions = [];
        this.listeners.clear();
    }

    /** The user asked for the server: forget the anchor, recover through every step including the server, and refresh. */
    async refind(): Promise<void> {
        this.setState({ ...this.state, refinding: true });
        try {
            const attempt = await this.input.tracker.refind();
            /**
             * WHEN refind recovered an anchor
             *   CONSEQUENCE
             *     rebuild the preview from the recovered location
             */
            if (attempt?.outcome === "recovered") {
                await this.refresh("refind");
                this.setState({ ...this.state, recovery: attempt });
                return;
            }
            this.setState({
                ...this.state,
                status: "error",
                recovery: attempt,
                /**
                 * CASES
                 *   - server gave an explanation -> include it in the error message
                 *   - no explanation             -> use the generic placement failure
                 */
                error: attempt?.explanation ? `The server could not place this redline: ${attempt.explanation}` : "The server could not place this redline.",
                cardBuildFailed: false,
            });
        } catch (error) {
            this.setState({ ...this.state, status: "error", error: AnchorLog.messageOf(error), cardBuildFailed: false });
        } finally {
            this.setState({ ...this.state, refinding: false });
        }
    }

    /** The card's bookmarks the page puts in a table cell, as the store resolved them. */
    private cellBookmarksOn(page: DocumentPage): string[] {
        return this.input.bookmarkNames.filter((bookmarkName) => (page.paragraphs.get(bookmarkName)?.tableId ?? null) !== null);
    }

    /** The table a bookmark's paragraph sits in, as the page last read it; null for a body paragraph or a bookmark the page does not hold. */
    private static tableReadOn(page: DocumentPage): (bookmarkName: string) => TableRead | null {
        return (bookmarkName) => {
            const tableId = page.paragraphs.get(bookmarkName)?.tableId ?? null;
            return tableId === null ? null : page.tables.get(tableId) ?? null;
        };
    }

    /**
     * The composed preview package: the built paragraphs with the card's table
     * changes written into the tables as the page holds them.
     *
     * CASES
     *   - a paragraph card                 -> the built package
     *   - a card writing into a table      -> the package composed over the page's tables
     *   - the composition fails            -> the built package, so the card still shows something
     */
    private previewOf(page: DocumentPage, built: BuiltProposal, stampedAt: string): string {
        const proposal = this.proposal;
        const cellBookmarks = this.cellBookmarksOn(page);
        if (proposal === null || !proposal.writesIntoTable(cellBookmarks)) return built.outcome.packageOoxml;
        try {
            return proposal.previewPackage(LiveProposal.tableReadOn(page), cellBookmarks, built, stampedAt).packageOoxml;
        } catch (error) {
            AnchorLog.record({ kind: "error", bookmarkNames: this.input.bookmarkNames, step: "table preview", message: AnchorLog.messageOf(error) });
            return built.outcome.packageOoxml;
        }
    }

    /**
     * The page the store read.
     *
     * CASES
     *   - the card is stopped, a body swap is running, or the page does not cover
     *     the card yet                                            -> wait for the next page
     *   - a bookmark of the card is gone from the page, and only
     *     views that read the page hold the card                  -> nothing: it keeps the preview it has,
     *                                                                and reads Word for no recovery
     *   - a bookmark of the card is gone from the page            -> recover through the tracker,
     *                                                                no more than once per cool-down
     *                                                                for a card already lost
     *   - the card's paragraphs and tables read as last built     -> nothing: the change was elsewhere
     *   - otherwise                                               -> build and publish from the page
     */
    private onPage(page: DocumentPage): void {
        if (this.disposed || this.replacingBody || !page.completed) return;
        const { bookmarkNames } = this.input;
        if (!bookmarkNames.every((bookmarkName) => page.coveredBookmarks.has(bookmarkName))) return;
        const reads = bookmarkNames.flatMap((bookmarkName) => {
            const read = page.paragraphs.get(bookmarkName);
            return read === undefined ? [] : [read];
        });
        if (reads.length < bookmarkNames.length) {
            /**
             * WHY a drawer never recovers
             *   The drawer holds every card of the chat. A document that lost them all
             *   (another copy of the file is open) would send each card through recovery,
             *   a Word read apiece, on every change. Recovery runs for a card a chat card
             *   holds, and for Apply.
             */
            if (!this.recoversLostParagraph) return;
            if (this.state.status === "error" && Date.now() - this.lastRefreshedAtMs < LiveProposal.LOST_CARD_EVENT_COOL_DOWN_MS) return;
            void this.refresh(this.state.refreshCount === 0 ? "seed" : "event");
            return;
        }
        const inputsKey = [
            ...reads.map((read) => read.paragraphOoxml),
            ...reads.map((read) => (read.tableId === null ? "" : page.tables.get(read.tableId)?.packageOoxml ?? "")),
        ].join("\u0000");
        if (inputsKey === this.lastBuiltInputsKey) return;
        this.lastBuiltInputsKey = inputsKey;
        /**
         * WHEN this card's build throws outside the engine (a paragraph read that does not parse)
         *   CONSEQUENCE
         *     the card shows the error. The page store calls every card from one loop, so an
         *     error let out of here would keep the cards after it, and the drawer, from the page.
         */
        try {
            this.buildFromPage(page, reads, this.state.status === "live" ? "event" : "seed");
        } catch (error) {
            AnchorLog.record({ kind: "error", bookmarkNames, step: "proposal from page", message: AnchorLog.messageOf(error) });
            this.setState({ ...this.state, status: "error", error: AnchorLog.messageOf(error), bookmarkLost: false, cardBuildFailed: true, lastUpdatedAtMs: Date.now() });
        }
    }

    /**
     * Builds and publishes the card from the page: its paragraphs in document order
     * as one range, each the paragraph alone (a cell paragraph comes wrapped in its
     * table from Word, and the builder pairs card texts with paragraphs by position).
     */
    private buildFromPage(page: DocumentPage, reads: PageParagraphRead[], source: LiveProposalSource): void {
        const inDocumentOrder = [...reads].sort((left, right) => left.paragraphIndex - right.paragraphIndex);
        const anchored: AnchoredRead = {
            rangeOoxml: OoxmlRange.packageAround(inDocumentOrder.map((read) => new OoxmlRange(read.paragraphOoxml).paragraphXml(0)).join("")),
            paragraphIds: [],
            uniqueLocalIds: inDocumentOrder.map((read) => read.uniqueLocalId),
            coveredSpans: [],
            bookmarkRepairCount: 0,
            elapsedMs: 0,
        };
        const stampedAt = new Date().toISOString();
        const buildResult = this.buildProposal(anchored, source, stampedAt);
        if (buildResult === null) return;
        this.publishProposal(anchored, source, stampedAt, buildResult.built, buildResult.engineMs, this.previewOf(page, buildResult.built, stampedAt));
    }

    private setState(next: LiveProposalState): void {
        /**
         * WHEN disposal happened before this publish
         *   CONSEQUENCE
         *     drop the state update
         */
        if (this.disposed) return;
        this.state = next;
        for (const listener of Array.from(this.listeners)) listener(next);
    }

    /** Builds the proposal for a read; null when construction never produced a proposal or the engine failed, which is published as the error state. */
    private buildProposal(anchored: AnchoredRead, source: LiveProposalSource, stampedAt: string): { built: BuiltProposal; engineMs: number } | null {
        /**
         * WHEN construction failed to create an anchored proposal
         *   CONSEQUENCE
         *     do not publish, even if reconcile returned paragraphs later
         */
        if (!this.proposal) return null;
        const engineStarted = performance.now();
        try {
            const built = this.proposal.build({ anchored, placement: this.input.tracker.placement, stampedAt });
            void RedlineRegionDisagreementReport.record({ verdict: built.outcome.regionVerdict, readPackageOoxml: anchored.rangeOoxml, redline: this.input.redline, coveredSpans: anchored.coveredSpans, where: "preview" });
            return { built, engineMs: performance.now() - engineStarted };
        } catch (error) {
            AnchorLog.record({ kind: "error", bookmarkNames: this.input.bookmarkNames, step: "proposal engine", message: AnchorLog.messageOf(error) });
            this.setState({
                ...this.state,
                status: "error",
                error: AnchorLog.messageOf(error),
                bookmarkLost: false,
                cardBuildFailed: true,
                lastSource: source,
                lastUpdatedAtMs: Date.now(),
                lastReadMs: anchored.elapsedMs,
            });
            return null;
        }
    }

    /** Publishes a built proposal and its composed preview package, with the user's edit laid over the composed package. */
    private publishProposal(anchored: AnchoredRead, source: LiveProposalSource, stampedAt: string, built: BuiltProposal, engineMs: number, composedPackageOoxml: string): void {
        const { tracker, bookmarkNames } = this.input;
        if (!this.proposal) return;
        try {
            const placement = tracker.placement;
            const tableChangeLines = this.proposal.tableChangePreviewLines(anchored);
            /**
             * EXAMPLE
             *   two words replaced and one row added -> baseChangeCount = 2 + 1 = 3, so the panel
             *   shows the preview rather than "already reads as this redline proposes"
             */
            const baseChangeCount = built.outcome.changeCount + tableChangeLines.length;
            this.lastComposed = { packageOoxml: composedPackageOoxml, baseChangeCount, stampedAt: built.outcome.stampedAt };
            const layered = this.withEditLayer(composedPackageOoxml, built.outcome.stampedAt);
            AnchorLog.record({
                kind: "proposal",
                bookmarkNames,
                source,
                placementKind: placement.kind,
                paragraphsRead: anchored.uniqueLocalIds.length,
                coveredSpans: anchored.coveredSpans,
                changeCount: built.outcome.changeCount,
                readMs: Math.round(anchored.elapsedMs),
                engineMs: Math.round(engineMs),
                liveSkeleton: built.outcome.changeCount > 0 ? new OoxmlRange(anchored.rangeOoxml).revisionSkeleton() : null,
                proposalSkeleton: built.outcome.changeCount > 0 ? new OoxmlRange(built.outcome.packageOoxml).revisionSkeleton() : null,
            });
            this.setState({
                ...this.state,
                status: "live",
                previewPackageOoxml: layered.packageOoxml,
                proposalPackageOoxml: composedPackageOoxml,
                changeCount: baseChangeCount + layered.layerChangeCount,
                tableChangeLines,
                placement,
                stampedAt: built.outcome.stampedAt,
                error: null,
                serverAwaitsUserAction: false,
                bookmarkLost: false,
                cardBuildFailed: false,
                lastSource: source,
                lastUpdatedAtMs: Date.now(),
                lastReadMs: anchored.elapsedMs,
                lastEngineMs: engineMs,
                refreshCount: this.state.refreshCount + 1,
            });
        } catch (error) {
            AnchorLog.record({ kind: "error", bookmarkNames, step: "proposal engine", message: AnchorLog.messageOf(error) });
            this.setState({
                ...this.state,
                status: "error",
                error: AnchorLog.messageOf(error),
                bookmarkLost: false,
                cardBuildFailed: true,
                lastSource: source,
                lastUpdatedAtMs: Date.now(),
                lastReadMs: anchored.elapsedMs,
            });
        }
    }

    /**
     * Recovery through the anchor tracker: the one path on which the card reads Word
     * itself, taken when a bookmark of the card is gone from the page (`onPage`), on
     * Refind, and when a recovery was asked for while one ran.
     */
    private refresh(source: LiveProposalSource): Promise<void> {
        /**
         * WHEN another refresh is already reconciling
         *   CONSEQUENCE
         *     record the latest source and reuse the in-flight run
         */
        if (this.inFlight) {
            this.pendingSource = source;
            return this.inFlight;
        }
        const triggerFor = (refreshSource: LiveProposalSource): RecoveryTrigger => {
            /**
             * WHEN the refresh source is "seed"
             *   CONSEQUENCE
             *     use the "loss" recovery trigger -> there is no current anchored paragraph yet
             */
            if (refreshSource === "seed") return "loss";
            /**
             * WHEN the refresh source is "refind"
             *   CONSEQUENCE
             *     use the "refind" trigger -> this path may continue to the server step
             */
            if (refreshSource === "refind") return "refind";
            return "event";
        };
        let refreshSlotHeld = false;
        const run = (async () => {
            const tracker = this.input.tracker;
            await LiveProposal.acquireRefreshSlot(this.input.word);
            refreshSlotHeld = true;
            /**
             * WHEN the card was stopped while waiting at the gate
             *   CONSEQUENCE
             *     nothing is read; the slot is released below
             */
            if (this.disposed) return;
            let anchored: AnchoredRead | null;
            try {
                anchored = await tracker.reconcile(triggerFor(source));
            } catch (error) {
                AnchorLog.record({ kind: "error", bookmarkNames: this.input.bookmarkNames, step: "reconcile", message: AnchorLog.messageOf(error) });
                this.setState({ ...this.state, status: "error", error: AnchorLog.messageOf(error), cardBuildFailed: false });
                return;
            }
            /**
             * WHEN the card was stopped while Word was reading
             *   CONSEQUENCE
             *     abandon the reconcile result
             */
            if (this.disposed) return;
            /**
             * WHEN reconcile returned no anchored paragraph
             *   CONSEQUENCE
             *     enter the error state instead of publishing a preview
             */
            if (!anchored) {
                const attempt = tracker.lastAttempt();
                const serverAwaitsUserAction = attempt === null || attempt.tier !== "server";
                this.setState({
                    ...this.state,
                    status: "error",
                    recovery: attempt,
                    serverAwaitsUserAction,
                    bookmarkLost: true,
                    cardBuildFailed: false,
                    /**
                     * CASES
                     *   - recovery reported ambiguity with detail -> include that detail
                     *   - recovery reported ambiguity without detail
                     *       -> say "more than one paragraph matches"
                     */
                    error: attempt?.outcome === "ambiguous"
                        ? `The paragraph this redline targets could not be re-anchored: ${attempt.explanation ?? "more than one paragraph matches"}.`
                        : "The paragraph this redline targets is no longer in the document.",
                });
                return;
            }
            /**
             * WHEN this reconcile repaired one or more bookmarks
             *   CONSEQUENCE
             *     add that count to the running repair total
             */
            if (anchored.bookmarkRepairCount > 0) {
                const updatedBookmarkRepairCount = this.state.bookmarkRepairCount + anchored.bookmarkRepairCount;
                const lastRecoveryAttempt = tracker.lastAttempt();
                const nextRecoveredProposalState = { ...this.state, bookmarkRepairCount: updatedBookmarkRepairCount, recovery: lastRecoveryAttempt };
                this.setState(nextRecoveredProposalState);
            }
            const stampedAt = new Date().toISOString();
            const buildResult = this.buildProposal(anchored, source, stampedAt);
            if (buildResult === null) return;
            /**
             * The tables come from the page as the store last read it; recovery may have
             * laid the bookmark back on a paragraph the page has not read yet, so the page
             * is asked to read again and the card rebuilds from it when it arrives.
             */
            this.lastBuiltInputsKey = null;
            this.publishProposal(anchored, source, stampedAt, buildResult.built, buildResult.engineMs, this.previewOf(this.input.pages.page, buildResult.built, stampedAt));
            this.input.pages.touch();
        })().finally(() => {
            if (refreshSlotHeld) LiveProposal.releaseRefreshSlot();
            this.lastRefreshedAtMs = Date.now();
            this.inFlight = null;
            const pending = this.pendingSource;
            this.pendingSource = null;
            const queuedRefreshIsMissing = pending === null;
            const liveProposalIsDisposed = this.disposed;
            const queuedRefreshShouldBeDropped = queuedRefreshIsMissing || liveProposalIsDisposed;
            /**
             * CASES
             *   - no later request was queued -> nothing to replay
             *   - disposed before replay       -> drop the queued work
             */
            if (queuedRefreshShouldBeDropped) return;
            /** A recovery asked for while one ran goes again now; the page store already coalesces document changes. */
            void this.refresh(pending);
        });
        this.inFlight = run;
        return run;
    }
}
